(() => {
  const LOG_PREFIX = '[SA Gallery]';
  const log = (...args) => console.log(LOG_PREFIX, ...args);
  const warn = (...args) => console.warn(LOG_PREFIX, ...args);

  const normalizeImageKey = (src) => {
    if (!src) return '';
    try {
      const path = decodeURIComponent(String(src).split('?')[0]);
      const file = path.split('/').pop() || '';
      return file.replace(/_\d+x\d*(?=\.[a-zA-Z]+$)/, '');
    } catch (error) {
      return '';
    }
  };

  const parseVariantMediaMap = (gallery) => {
    if (!gallery) return [];
    const script = gallery.querySelector('[data-sa-variant-media-map]');
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      warn('variant media map JSON parse failed', error);
      return [];
    }
  };

  const getSelectedOptionValues = (productInfo) =>
    Array.from(
      productInfo?.querySelectorAll(
        'variant-selects input:checked, variant-selects select option[selected]',
      ) || [],
    ).map((element) => element.value);

  const findMappedVariantMedia = (gallery, productInfo) => {
    const map = parseVariantMediaMap(gallery);
    if (!map.length) return null;
    const selected = getSelectedOptionValues(productInfo);
    if (!selected.length) return null;
    return (
      map.find(
        (entry) =>
          Array.isArray(entry.options) &&
          entry.options.length === selected.length &&
          entry.options.every((option, index) => option === selected[index]),
      ) || null
    );
  };

  const resolveActiveMediaIndex = (gallery, mediaId, imageSrc) => {
    const slides = Array.from(gallery.querySelectorAll('[data-sa-slide-index]'));
    const items = slides.length
      ? slides
      : Array.from(gallery.querySelectorAll('[data-media-id][data-sa-thumb-index]'));
    const availableIds = items.map((item) => String(item.dataset.mediaId));

    const rawId = mediaId != null && mediaId !== '' ? String(mediaId) : '';
    const candidateIds = new Set();
    if (rawId) {
      candidateIds.add(rawId);
      candidateIds.add(rawId.split('-').pop());
      const trailing = rawId.match(/(\d+)\s*$/);
      if (trailing) candidateIds.add(trailing[1]);
    }

    let index = -1;
    if (candidateIds.size) {
      index = items.findIndex((item) => candidateIds.has(String(item.dataset.mediaId)));
    }

    let matchedBy = index >= 0 ? 'mediaId' : null;
    if (index < 0) {
      const key = normalizeImageKey(imageSrc);
      if (key) {
        index = items.findIndex((item) => {
          const image = item.querySelector('img');
          if (!image) return false;
          return [image.currentSrc, image.src].some((src) => normalizeImageKey(src) === key);
        });
        if (index >= 0) matchedBy = 'imageSrc';
      }
    }

    return { index, items, availableIds, candidateIds: [...candidateIds], matchedBy };
  };

  const applyActiveMedia = (gallery, mediaId, imageSrc, reason) => {
    // Refresh cached slide/thumb lists in case DOM changed.
    gallery.slides = Array.from(gallery.querySelectorAll('[data-sa-slide-index]'));
    gallery.thumbs = Array.from(gallery.querySelectorAll('[data-sa-thumb-index]'));

    const resolved = resolveActiveMediaIndex(gallery, mediaId, imageSrc);
    log('setActiveMedia', {
      reason,
      galleryId: gallery.id,
      mediaId,
      imageSrc,
      matchedBy: resolved.matchedBy,
      index: resolved.index,
      activeIndex: gallery.activeIndex,
      availableIds: resolved.availableIds,
      candidateIds: resolved.candidateIds,
    });

    if (resolved.index < 0) {
      warn('media not found in gallery DOM — check data-media-id vs featured_media.id', {
        reason,
        mediaId,
        imageSrc,
        availableIds: resolved.availableIds,
      });
      return false;
    }

    if (typeof gallery.activate === 'function') {
      gallery.activate(resolved.index);
    } else {
      warn('gallery.activate missing; cannot switch slide');
      return false;
    }

    log('activated', { reason, index: resolved.index, galleryId: gallery.id });
    return true;
  };

  class SaProductMediaGallery extends HTMLElement {
    connectedCallback() {
      this.isGridMode = this.classList.contains('sa-product-media-gallery--thumb_grid_2col');
      this.thumbs = Array.from(this.querySelectorAll('[data-sa-thumb-index]'));
      this.slides = Array.from(this.querySelectorAll('[data-sa-slide-index]'));
      this.thumbsContainer = this.querySelector('[data-sa-thumbs]');
      this.stageZone = this.querySelector('[data-sa-stage-zone]');
      this.stage = this.querySelector('[data-sa-stage]');
      this.zoomInplace = this.querySelector('[data-sa-zoom-inplace]');
      this.zoomImage = this.querySelector('[data-sa-zoom-image]');
      this.modal = this.querySelector('[data-sa-modal]');
      this.modalStage = this.modal?.querySelector('[data-sa-modal-stage]');
      this.modalZoomInplace = this.modal?.querySelector('[data-sa-modal-zoom-inplace]');
      this.modalZoomImage = this.modal?.querySelector('[data-sa-modal-zoom-image]');
      this.modalThumbs = Array.from(this.modal?.querySelectorAll('[data-sa-modal-thumb-index]') || []);
      this.modalSlides = Array.from(this.modal?.querySelectorAll('[data-sa-modal-slide-index]') || []);
      this.zoomScale = 2.5;
      this.enableLightbox = this.dataset.saEnableLightbox !== 'false';
      this.mainZoomReady = false;
      this.modalZoomReady = false;
      this.activeIndex = this.thumbs.findIndex((thumb) => thumb.classList.contains('is-active'));
      if (this.activeIndex < 0) this.activeIndex = 0;
      this.modalActiveIndex = this.activeIndex;
      this.lastModalPointer = { clientX: 0, clientY: 0 };

      this.onStageMouseMove = (event) => this.onZoomMouseMove('main', event);
      this.onStageMouseLeave = () => this.destroyZoom('main');
      this.onModalStageMouseMove = (event) => this.onZoomMouseMove('modal', event);
      this.onModalStageMouseLeave = () => this.destroyZoom('modal');
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onModalCloseClick = this.onModalCloseClick.bind(this);

      log('connected', {
        id: this.id,
        slideCount: this.slides.length,
        mediaIds: this.slides.map((slide) => slide.dataset.mediaId),
        mapSize: parseVariantMediaMap(this).length,
      });

      if (this.modal && this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      this.thumbs.forEach((thumb, index) => {
        if (this.isGridMode) return;

        thumb.addEventListener('mouseenter', () => this.activate(index));
        thumb.addEventListener('focus', () => this.activate(index));
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          this.activate(index);
        });
      });

      if (this.isGridMode) {
        this.querySelectorAll('[data-sa-grid-cell][data-media-type="image"]').forEach((cell) => {
          cell.addEventListener('click', (event) => {
            event.preventDefault();
            const index = Number.parseInt(cell.dataset.saThumbIndex, 10);
            if (Number.isNaN(index)) return;

            if (index !== this.activeIndex) {
              this.setGalleryIndex(index);
            }

            if (this.enableLightbox) {
              this.openModal();
            }
          });
        });

        this.querySelectorAll('[data-sa-grid-cell] [id^="Deferred-Poster-"]').forEach((poster) => {
          poster.addEventListener('click', () => {
            const cell = poster.closest('[data-sa-grid-cell]');
            if (!cell) return;

            const index = Number.parseInt(cell.dataset.saThumbIndex, 10);
            if (Number.isNaN(index) || index === this.activeIndex) return;
            this.setGalleryIndex(index);
          });
        });
      }

      if (!this.isGridMode) {
        this.querySelectorAll('[data-sa-viewer-nav]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.step(button.dataset.saViewerNav);
          });
        });
        this.bindStageSwipe();
      }

      if (!this.isGridMode) {
        this.querySelectorAll('[data-sa-thumb-nav]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.saThumbNav;
          this.scrollThumbs(direction);
        });
      });
      }

      if (!this.isGridMode) {
        this.stage?.addEventListener('mouseenter', (event) => this.onZoomMouseEnter('main', event));
        this.stage?.addEventListener('mousemove', this.onStageMouseMove);
        this.stage?.addEventListener('mouseleave', this.onStageMouseLeave);
        this.stage?.addEventListener('click', (event) => this.onStageClick(event));
      }

      this.modalStage?.addEventListener('mouseenter', (event) => this.onZoomMouseEnter('modal', event));
      this.modalStage?.addEventListener('mousemove', (event) => {
        this.lastModalPointer = { clientX: event.clientX, clientY: event.clientY };
        this.onModalStageMouseMove(event);
      });
      this.modalStage?.addEventListener('mouseleave', this.onModalStageMouseLeave);

      this.modalThumbs.forEach((thumb, index) => {
        thumb.addEventListener('mouseenter', () => this.activateModal(index));
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.activateModal(index);
        });
      });

      this.modal?.querySelectorAll('[data-sa-modal-close]').forEach((button) => {
        button.addEventListener('click', this.onModalCloseClick);
      });

      this.thumbsContainer?.addEventListener('scroll', () => this.updateThumbNavState());

      if (!this.isGridMode && this.stageZone) {
        this.stageZoneResizeObserver = new ResizeObserver(() => {
          if (this.stage?.classList.contains('is-height-animating')) return;
          this.syncStageHeight(this.slides[this.activeIndex], false);
        });
        this.stageZoneResizeObserver.observe(this.stageZone);
      }

      window.addEventListener('resize', () => {
        if (!this.isGridMode) {
          this.updateThumbNavState();
          this.syncStageHeight(this.slides[this.activeIndex], false);
          this.updateStageZoomable();
          this.destroyZoom('main');
        }

        this.syncModalStageHeight(this.modalSlides[this.modalActiveIndex], false);
        this.updateModalStageZoomable();
        this.destroyZoom('modal');
      });

      if (!this.isGridMode) {
        this.updateThumbNavState();
        this.updateViewerNavStyle();
        this.updateStageZoomable();
        this.syncStageHeight(this.slides[this.activeIndex], false);
        this.scrollActiveThumbIntoView(false);
      } else {
        this.updateModalStageZoomable();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.stageZoneResizeObserver?.disconnect();
      this.destroyZoom('main');
      this.destroyZoom('modal');
      // The modal is moved to document.body in connectedCallback.
      // Remove it when the gallery itself is replaced to avoid stale duplicate modals.
      this.modal?.remove();
      document.body.style.overflow = '';
    }

    getZoomContext(type) {
      if (type === 'modal') {
        return {
          type: 'modal',
          stage: this.modalStage,
          zoomInplace: this.modalZoomInplace,
          zoomImage: this.modalZoomImage,
          getActiveImage: () => this.getModalActiveImage(),
          isReadyKey: 'modalZoomReady',
        };
      }

      return {
        type: 'main',
        stage: this.stage,
        zoomInplace: this.zoomInplace,
        zoomImage: this.zoomImage,
        getActiveImage: () => this.getActiveImage(),
        isReadyKey: 'mainZoomReady',
      };
    }

    getActiveImage() {
      const activeSlide = this.slides[this.activeIndex];
      if (!activeSlide) return null;
      if (activeSlide.querySelector('video, deferred-media, product-model')) return null;
      return activeSlide.querySelector('.sa-product-media-gallery__media--zoomable');
    }

    getModalActiveImage() {
      const activeSlide = this.modalSlides[this.modalActiveIndex];
      if (!activeSlide) return null;
      if (activeSlide.querySelector('video, deferred-media, product-model')) return null;
      return activeSlide.querySelector('.sa-product-media-gallery__media--zoomable');
    }

    isDesktop() {
      return window.innerWidth >= 1000;
    }

    isModalOpen() {
      return this.modal && !this.modal.hasAttribute('hidden');
    }

    updateStageZoomable() {
      if (!this.stage) return;
      const activeSlide = this.slides[this.activeIndex];
      const image = this.getActiveImage();
      const isVideo = activeSlide?.dataset.mediaType && activeSlide.dataset.mediaType !== 'image';

      this.stage.dataset.saStageZoomable =
        image && this.isDesktop() && !this.isModalOpen() ? 'true' : 'false';
      this.stage.dataset.saStageVideo = isVideo ? 'true' : 'false';
    }

    updateModalStageZoomable() {
      if (!this.modalStage) return;
      const image = this.getModalActiveImage();
      this.modalStage.dataset.saModalStageZoomable = image && this.isDesktop() ? 'true' : 'false';
    }

    onZoomMouseEnter(type, event) {
      if (!this.isDesktop()) return;
      if (type === 'main' && this.isModalOpen()) return;

      const context = this.getZoomContext(type);
      const image = context.getActiveImage();
      if (!image || !context.stage || !context.zoomInplace || !context.zoomImage) return;

      const zoomSrc = image.dataset.zoomSrc || image.currentSrc || image.src;
      this[context.isReadyKey] = false;

      const showZoom = () => {
        this[context.isReadyKey] = true;
        context.zoomInplace.classList.add('is-active');
        context.zoomInplace.setAttribute('aria-hidden', 'false');
        context.stage.classList.add('is-zooming');
        this.moveZoom(context, event);
      };

      if (context.zoomImage.getAttribute('src') !== zoomSrc) {
        context.zoomImage.onload = () => showZoom();
        context.zoomImage.setAttribute('src', zoomSrc);
      } else if (context.zoomImage.complete && context.zoomImage.naturalWidth) {
        showZoom();
      } else {
        context.zoomImage.onload = () => showZoom();
      }

      context.zoomImage.alt = image.alt || '';
    }

    onZoomMouseMove(type, event) {
      const context = this.getZoomContext(type);
      if (!this[context.isReadyKey]) return;
      this.moveZoom(context, event);
    }

    moveZoom(context, event) {
      if (!context.zoomInplace || !context.zoomImage || !context.stage) return;

      const stageRect = context.stage.getBoundingClientRect();
      const naturalW = context.zoomImage.naturalWidth;
      const naturalH = context.zoomImage.naturalHeight;
      if (!naturalW || !naturalH) return;

      const cx = Math.max(0, Math.min(event.clientX - stageRect.left, stageRect.width));
      const cy = Math.max(0, Math.min(event.clientY - stageRect.top, stageRect.height));

      const zoomW = stageRect.width * this.zoomScale;
      const zoomH = (naturalH / naturalW) * zoomW;

      context.zoomImage.style.width = `${zoomW}px`;
      context.zoomImage.style.height = `${zoomH}px`;

      const maxLeft = stageRect.width - zoomW;
      const maxTop = stageRect.height - zoomH;
      let left = -(cx / stageRect.width) * (zoomW - stageRect.width);
      let top = -(cy / stageRect.height) * (zoomH - stageRect.height);
      left = Math.max(maxLeft, Math.min(0, left));
      top = Math.max(maxTop, Math.min(0, top));

      context.zoomImage.style.left = `${left}px`;
      context.zoomImage.style.top = `${top}px`;
    }

    destroyZoom(type) {
      const context = this.getZoomContext(type);
      this[context.isReadyKey] = false;
      context.zoomInplace?.classList.remove('is-active');
      context.zoomInplace?.setAttribute('aria-hidden', 'true');
      context.stage?.classList.remove('is-zooming');
    }

    bindStageSwipe() {
      if (!this.stage || this.stageSwipeBound) return;
      this.stageSwipeBound = true;

      const finishSwipe = (event) => {
        if (!this.swipeState || event.pointerId !== this.swipeState.id) return;
        const dx = event.clientX - this.swipeState.x;
        const dy = event.clientY - this.swipeState.y;
        this.swipeState = null;

        if (Math.abs(dx) <= 10) return;

        this.suppressStageClick = true;
        window.setTimeout(() => {
          this.suppressStageClick = false;
        }, 500);

        if (Math.abs(dx) < 24 || Math.abs(dx) <= Math.abs(dy)) return;
        this.step(dx < 0 ? 'next' : 'prev');
      };

      this.onStagePointerDown = (event) => {
        if (this.isDesktop() || this.slides.length < 2) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('[data-sa-viewer-nav]')) return;

        this.swipeState = { x: event.clientX, y: event.clientY, id: event.pointerId };
        try {
          this.stage.setPointerCapture(event.pointerId);
        } catch (error) {
          // Pointer can already be released if the browser takes the gesture.
        }
      };

      this.onStagePointerMove = (event) => {
        if (!this.swipeState || event.pointerId !== this.swipeState.id) return;
        const dx = event.clientX - this.swipeState.x;
        const dy = event.clientY - this.swipeState.y;
        if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) event.preventDefault();
      };

      this.onStagePointerUp = (event) => finishSwipe(event);
      this.onStagePointerCancel = (event) => finishSwipe(event);

      this.stage.addEventListener('pointerdown', this.onStagePointerDown);
      this.stage.addEventListener('pointermove', this.onStagePointerMove);
      this.stage.addEventListener('pointerup', this.onStagePointerUp);
      this.stage.addEventListener('pointercancel', this.onStagePointerCancel);
    }

    step(direction) {
      if (this.slides.length < 2) return;
      const nextIndex =
        direction === 'next'
          ? (this.activeIndex + 1) % this.slides.length
          : (this.activeIndex - 1 + this.slides.length) % this.slides.length;
      this.activate(nextIndex);
    }

    onStageClick(event) {
      if (this.suppressStageClick) {
        this.suppressStageClick = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.target.closest('[data-sa-viewer-nav]')) return;
      if (event.target.closest('video, iframe, deferred-media, .deferred-media__poster, product-model')) return;

      const activeSlide = this.slides[this.activeIndex];
      if (activeSlide?.dataset.mediaType && activeSlide.dataset.mediaType !== 'image') return;

      if (!this.enableLightbox) return;
      this.openModal();
    }

    onModalCloseClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.closeModal();
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') this.closeModal();
    }

    openModal() {
      if (!this.modal) return;

      this.destroyZoom('main');
      this.modalActiveIndex = this.activeIndex;
      this.syncModalSlide(this.modalActiveIndex);
      this.modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.updateStageZoomable();
      this.updateModalStageZoomable();
      requestAnimationFrame(() => {
        this.syncModalStageHeight(this.modalSlides[this.modalActiveIndex], false);
      });

      const activeModalThumb = this.modalThumbs[this.modalActiveIndex];
      activeModalThumb?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    closeModal() {
      if (!this.modal) return;

      this.destroyZoom('modal');
      this.modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.pauseModalMedia();
      this.updateStageZoomable();
    }

    activateModal(index) {
      if (!this.modalSlides.length || index < 0 || index >= this.modalSlides.length) return;
      if (index === this.modalActiveIndex) return;

      this.destroyZoom('modal');
      this.syncModalSlide(index);

      if (index !== this.activeIndex) {
        this.setGalleryIndex(index);
      }

      if (this.modalStage?.matches(':hover')) {
        this.onZoomMouseEnter('modal', this.lastModalPointer);
      }
    }

    syncModalSlide(index) {
      this.modalActiveIndex = index;

      this.modalSlides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.modalThumbs.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.updateModalStageZoomable();
      this.syncModalStageHeight(this.modalSlides[index]);
      this.pauseModalMedia();
    }

    syncStageHeight(slide, animate = true) {
      if (!this.stage || !slide) return;
      const ratio = parseFloat(slide.dataset.aspectRatio) || 1;
      const width = this.stageZone?.offsetWidth || this.stage.offsetWidth;
      if (!width) return;
      const targetHeight = Math.round(width / ratio);

      if (!animate) {
        this.stage.classList.remove('is-height-animating');
        this.stage.style.transition = 'none';
        this.stage.style.height = `${targetHeight}px`;
        void this.stage.offsetHeight;
        this.stage.style.removeProperty('transition');
        return;
      }

      const currentHeight = this.stage.offsetHeight;
      if (Math.abs(currentHeight - targetHeight) <= 1) {
        this.stage.classList.remove('is-height-animating');
        this.stage.style.height = `${targetHeight}px`;
        return;
      }

      this.stage.classList.add('is-height-animating');

      const onTransitionEnd = (event) => {
        if (event.target !== this.stage || event.propertyName !== 'height') return;
        this.stage.removeEventListener('transitionend', onTransitionEnd);
        this.stage.classList.remove('is-height-animating');
      };

      this.stage.removeEventListener('transitionend', onTransitionEnd);
      this.stage.addEventListener('transitionend', onTransitionEnd);

      this.stage.style.height = `${currentHeight}px`;
      void this.stage.offsetHeight;

      requestAnimationFrame(() => {
        this.stage.style.height = `${targetHeight}px`;
      });
    }

    syncModalStageHeight(slide, animate = true) {
      if (!this.modalStage || !slide) return;
      if (!this.isDesktop()) {
        this.modalStage.style.removeProperty('height');
        return;
      }
      const ratio = parseFloat(slide.dataset.aspectRatio) || 1;
      const width = this.modalStage.offsetWidth;
      if (!width) return;
      const height = Math.round(width / ratio);

      if (!animate) {
        this.modalStage.style.transition = 'none';
        this.modalStage.style.height = `${height}px`;
        void this.modalStage.offsetHeight;
        this.modalStage.style.removeProperty('transition');
        return;
      }

      const currentHeight = this.modalStage.offsetHeight;
      this.modalStage.style.height = `${currentHeight}px`;
      requestAnimationFrame(() => {
        this.modalStage.style.height = `${height}px`;
      });
    }

    pauseModalMedia() {
      this.modal?.querySelectorAll('video').forEach((video) => {
        video.pause();
      });
      document.dispatchEvent(new CustomEvent('sa:product-media:paused'));
    }

    setActiveMedia(mediaId, imageSrc) {
      this.classList.add('sa-product-media-gallery--variant-switching');
      const switched = applyActiveMedia(this, mediaId, imageSrc, 'gallery.setActiveMedia');
      requestAnimationFrame(() => {
        this.classList.remove('sa-product-media-gallery--variant-switching');
      });
      return switched;
    }

    setGalleryIndex(index) {
      if (!this.slides.length || index < 0 || index >= this.slides.length) return;
      if (index === this.activeIndex) return;

      this.destroyZoom('main');
      this.pauseAllMedia();

      const nextSlide = this.slides[index];

      if (this.stage) {
        const currentHeight = this.stage.offsetHeight;
        this.stage.style.height = `${currentHeight}px`;
        this.stage.classList.add('is-height-animating');
      }

      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.thumbs.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.activeIndex = index;
      this.scrollActiveThumbIntoView();
      this.updateThumbNavState();
      this.updateViewerNavStyle();
      this.updateStageZoomable();
      this.syncStageHeight(
        nextSlide,
        !this.classList.contains('sa-product-media-gallery--variant-switching'),
      );
    }

    activate(index) {
      if (!this.slides.length || index < 0 || index >= this.slides.length) return;
      if (index === this.activeIndex) return;

      this.setGalleryIndex(index);

      if (this.isModalOpen() && index !== this.modalActiveIndex) {
        this.syncModalSlide(index);
      }
    }

    pauseAllMedia() {
      this.querySelectorAll('video').forEach((video) => {
        video.pause();
      });

      this.querySelectorAll('iframe').forEach((iframe) => {
        const src = iframe.getAttribute('src');
        if (src) iframe.setAttribute('src', src);
      });

      document.dispatchEvent(new CustomEvent('sa:product-media:paused'));
    }

    scrollThumbs(direction) {
      if (!this.thumbsContainer || !this.thumbs.length) return;

      const isHorizontal = this.isThumbsHorizontal();
      const activeThumb = this.thumbs[this.activeIndex] || this.thumbs[0];
      const thumbSize = isHorizontal
        ? activeThumb.offsetWidth + 12
        : activeThumb.offsetHeight + 12;
      const delta = direction === 'next' ? thumbSize : -thumbSize;

      if (isHorizontal) {
        this.thumbsContainer.scrollBy({ left: delta, behavior: 'smooth' });
      } else {
        this.thumbsContainer.scrollBy({ top: delta, behavior: 'smooth' });
      }
    }

    scrollActiveThumbIntoView(smooth = true) {
      const activeThumb = this.thumbs[this.activeIndex];
      if (!activeThumb || !this.thumbsContainer) return;

      activeThumb.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'nearest',
        inline: 'nearest',
      });
    }

    isThumbsHorizontal() {
      if (window.innerWidth < 1000) return true;
      return (
        this.classList.contains('sa-product-media-gallery--thumb_top') ||
        this.classList.contains('sa-product-media-gallery--thumb_bottom')
      );
    }

    updateThumbNavState() {
      if (!this.thumbsContainer) return;

      const isHorizontal = this.isThumbsHorizontal();
      const scrollPos = isHorizontal ? this.thumbsContainer.scrollLeft : this.thumbsContainer.scrollTop;
      const maxScroll = isHorizontal
        ? this.thumbsContainer.scrollWidth - this.thumbsContainer.clientWidth
        : this.thumbsContainer.scrollHeight - this.thumbsContainer.clientHeight;

      this.querySelectorAll('[data-sa-thumb-nav="prev"]').forEach((button) => {
        button.disabled = scrollPos <= 1;
      });
      this.querySelectorAll('[data-sa-thumb-nav="next"]').forEach((button) => {
        button.disabled = maxScroll <= 1 || scrollPos >= maxScroll - 1;
      });
    }

    updateViewerNavStyle() {
      if (this.slides.length <= 1) return;

      const lastIndex = this.slides.length - 1;

      this.querySelectorAll('[data-sa-viewer-nav="prev"]').forEach((button) => {
        button.dataset.saNavVariant = this.activeIndex === 0 ? 'white' : 'black';
      });

      this.querySelectorAll('[data-sa-viewer-nav="next"]').forEach((button) => {
        button.dataset.saNavVariant = this.activeIndex === lastIndex ? 'white' : 'black';
      });
    }
  }

  const Existing = customElements.get('sa-product-media-gallery');
  if (!Existing) {
    customElements.define('sa-product-media-gallery', SaProductMediaGallery);
    log('custom element defined');
  } else {
    // theme-dev hot reload: CE cannot redefine, so patch methods onto existing prototype
    Object.getOwnPropertyNames(SaProductMediaGallery.prototype).forEach((name) => {
      if (name === 'constructor') return;
      Existing.prototype[name] = SaProductMediaGallery.prototype[name];
    });
    log('custom element prototype upgraded for hot reload');
  }

  document.querySelectorAll('sa-product-media-gallery').forEach((gallery) => {
    gallery.bindStageSwipe?.();
  });

  const syncGalleriesInProductInfo = (productInfo, mediaId, imageSrc, reason) => {
    if (!productInfo) {
      warn('sync skipped: no product-info', { reason });
      return;
    }

    const galleries = productInfo.querySelectorAll('sa-product-media-gallery');
    log('sync galleries', {
      reason,
      mediaId,
      imageSrc,
      galleryCount: galleries.length,
      section: productInfo.dataset?.section,
    });

    if (!galleries.length) {
      warn('no sa-product-media-gallery inside product-info');
      return;
    }

    galleries.forEach((gallery) => applyActiveMedia(gallery, mediaId, imageSrc, reason));
  };

  const onOptionValueSelectionChange = ({ data } = {}) => {
    const event = data?.event;
    const target = data?.target;
    const productInfo = event?.target?.closest?.('product-info');
    if (!productInfo) {
      log('optionValueSelectionChange ignored: outside product-info');
      return;
    }

    const mapped = findMappedVariantMedia(
      productInfo.querySelector('sa-product-media-gallery'),
      productInfo,
    );
    const swatchImage = target?.closest?.('.sa-product-variant-picker__swatch')?.querySelector('img');
    // Color swatch carries data-variant-media-id; Size/Shipping rely on full-options map.
    const mediaId = target?.dataset?.variantMediaId || mapped?.mediaId || null;
    const imageSrc =
      target?.dataset?.variantImage || mapped?.image || swatchImage?.currentSrc || swatchImage?.src || null;

    log('optionValueSelectionChange', {
      optionName: target?.dataset?.optionName,
      optionValue: target?.value,
      dataVariantMediaId: target?.dataset?.variantMediaId || null,
      mappedMediaId: mapped?.mediaId || null,
      mediaId,
      imageSrc,
      selectedOptions: getSelectedOptionValues(productInfo),
    });

    if (!mediaId && !imageSrc) {
      warn('no mediaId/imageSrc from option click — Color swatch may lack data-variant-media-id');
      return;
    }

    syncGalleriesInProductInfo(productInfo, mediaId, imageSrc, 'optionValueSelectionChange');
  };

  const onVariantInputChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    const variantSelects = target.closest('variant-selects');
    const productInfo = target.closest('product-info');
    if (!variantSelects || !productInfo) return;

    const mapped = findMappedVariantMedia(
      productInfo.querySelector('sa-product-media-gallery'),
      productInfo,
    );
    const swatchImage = target.closest('.sa-product-variant-picker__swatch')?.querySelector('img');
    const mediaId = target.dataset.variantMediaId || mapped?.mediaId || null;
    const imageSrc =
      target.dataset.variantImage ||
      mapped?.image ||
      swatchImage?.currentSrc ||
      swatchImage?.src ||
      null;

    log('DOM variant input change', {
      eventType: event.type,
      targetId: target.id,
      optionName: target.dataset.optionName,
      optionValue: target.value,
      checked: target instanceof HTMLInputElement ? target.checked : undefined,
      dataVariantMediaId: target.dataset.variantMediaId || null,
      mappedVariantId: mapped?.id || null,
      mappedMediaId: mapped?.mediaId || null,
      resolvedMediaId: mediaId,
      selectedOptions: getSelectedOptionValues(productInfo),
    });

    if (!mediaId && !imageSrc) {
      warn('DOM change failed: no media data found for selected variant', {
        targetId: target.id,
        optionName: target.dataset.optionName,
        optionValue: target.value,
        selectedOptions: getSelectedOptionValues(productInfo),
        mapSize: parseVariantMediaMap(
          productInfo.querySelector('sa-product-media-gallery'),
        ).length,
      });
      return;
    }

    syncGalleriesInProductInfo(productInfo, mediaId, imageSrc, 'DOM variant input change');
  };

  const onVariantChange = ({ data } = {}) => {
    // Prefer featured_media.id (matches slide data-media-id). featured_image.id is a different ID space.
    const mediaId = data?.variant?.featured_media?.id || null;
    const imageSrc =
      data?.variant?.featured_media?.preview_image?.src ||
      data?.variant?.featured_image?.src ||
      null;

    log('variantChange', {
      sectionId: data?.sectionId,
      variantId: data?.variant?.id,
      featuredMediaId: data?.variant?.featured_media?.id,
      featuredImageId: data?.variant?.featured_image?.id,
      mediaId,
      imageSrc,
    });

    let productInfo = null;
    if (data?.sectionId) {
      productInfo =
        document.querySelector(`product-info[data-section="${data.sectionId}"]`) ||
        document.getElementById(`MainProduct-${data.sectionId}`);
    }
    if (!productInfo) productInfo = document.querySelector('product-info');

    if (!mediaId && !imageSrc) {
      warn('variantChange has no featured_media — variant may have no attached image');
      return;
    }

    syncGalleriesInProductInfo(productInfo, mediaId, imageSrc, 'variantChange');
  };

  if (typeof window.__saGalleryVariantSyncCleanup === 'function') {
    window.__saGalleryVariantSyncCleanup();
  }

  const unsubscribers = [];
  document.addEventListener('change', onVariantInputChange, true);

  const bindPubSub = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      warn('pubsub not ready yet, retrying…');
      requestAnimationFrame(bindPubSub);
      return;
    }

    unsubscribers.push(subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, onOptionValueSelectionChange));
    unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange));
    log('pubsub listeners bound', {
      optionValueSelectionChange: PUB_SUB_EVENTS.optionValueSelectionChange,
      variantChange: PUB_SUB_EVENTS.variantChange,
    });
  };

  bindPubSub();

  window.__saGalleryVariantSyncCleanup = () => {
    document.removeEventListener('change', onVariantInputChange, true);
    while (unsubscribers.length) {
      const unsubscribe = unsubscribers.pop();
      try {
        unsubscribe?.();
      } catch (error) {
        // ignore
      }
    }
  };
})();
